import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Save, X, Layers, Eye, EyeOff, GripVertical, Link as LinkIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function CollectionsManager({ authHeader }) {
  const [collections, setCollections] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', slug: '', description: '', image_url: '', tag: '', active: true, order: 0 });

  const fetchCollections = async () => {
    try {
      const res = await axios.get(`${API}/admin/collections`, { headers: { Authorization: authHeader } });
      setCollections(res.data);
    } catch { toast.error('Error al cargar colecciones'); }
  };

  useEffect(() => { fetchCollections(); }, []);

  const generateSlug = (name) => {
    return name.toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  };

  const openCreate = () => {
    setEditing('new');
    setForm({ name: '', slug: '', description: '', image_url: '', tag: '', active: true, order: collections.length });
  };

  const openEdit = (c) => {
    setEditing(c.id);
    setForm({ name: c.name, slug: c.slug, description: c.description, image_url: c.image_url, tag: c.tag, active: c.active, order: c.order });
  };

  const handleSave = async () => {
    if (!form.name || !form.slug) {
      toast.error('Nombre y slug son obligatorios');
      return;
    }
    // Ensure tag always equals slug
    const payload = { ...form, tag: form.slug };
    try {
      if (editing === 'new') {
        await axios.post(`${API}/admin/collections`, payload, { headers: { Authorization: authHeader } });
        // Auto-create navigation item under "eventos" menu
        try {
          await axios.post(`${API}/admin/navigation`, {
            label: form.name,
            url: `/coleccion/${form.slug}`,
            parent: 'eventos',
            order: 10,
            active: true,
            open_new_tab: false
          }, { headers: { Authorization: authHeader } });
        } catch {}
        toast.success('Colección creada y añadida al menú Eventos');
      } else {
        await axios.put(`${API}/admin/collections/${editing}`, payload, { headers: { Authorization: authHeader } });
        toast.success('Colección actualizada');
      }
      setEditing(null);
      fetchCollections();
    } catch { toast.error('Error al guardar'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta colección?')) return;
    try {
      await axios.delete(`${API}/admin/collections/${id}`, { headers: { Authorization: authHeader } });
      toast.success('Colección eliminada');
      fetchCollections();
    } catch { toast.error('Error al eliminar'); }
  };

  return (
    <div data-testid="collections-manager">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-white font-medium flex items-center gap-2">
          <Layers size={18} className="text-[var(--primary)]" />
          Colecciones de Eventos
        </h3>
        <Button onClick={openCreate} className="btn-primary" data-testid="create-collection-btn">
          <Plus size={16} className="mr-2" /> Nueva Colección
        </Button>
      </div>

      <p className="text-white/40 text-sm mb-6">
        Las colecciones agrupan eventos por etiqueta. Asigna la etiqueta a los eventos desde el formulario de evento.
      </p>

      {/* Edit/Create Form */}
      {editing && (
        <div className="bg-[var(--surface)] border border-[var(--primary)]/30 p-6 mb-6" data-testid="collection-form">
          <h4 className="text-white font-medium mb-4">{editing === 'new' ? 'Nueva Colección' : 'Editar Colección'}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50 mb-1 block">Nombre *</label>
              <Input
                value={form.name}
                onChange={(e) => {
                  const name = e.target.value;
                  const slug = generateSlug(name);
                  setForm(f => ({ ...f, name, slug, tag: slug }));
                }}
                placeholder="óh!pera summer"
                className="bg-transparent border-white/20 text-white rounded-none"
                data-testid="collection-name-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50 mb-1 block">Slug / Etiqueta (auto-generado)</label>
              <Input
                value={form.slug}
                onChange={(e) => {
                  const slug = e.target.value;
                  setForm(f => ({ ...f, slug, tag: slug }));
                }}
                placeholder="ohpera-summer"
                className="bg-transparent border-white/20 text-white rounded-none font-mono text-sm"
                data-testid="collection-slug-input"
              />
              <p className="text-white/30 text-xs mt-1">
                URL: /coleccion/<strong className="text-white/50">{form.slug || '...'}</strong> — Usa esta misma etiqueta en los eventos
              </p>
            </div>
            <div className="col-span-2 md:col-span-1">
              <label className="text-xs uppercase tracking-widest text-white/50 mb-1 block">Imagen Hero (URL)</label>
              <Input
                value={form.image_url}
                onChange={(e) => setForm(f => ({ ...f, image_url: e.target.value }))}
                placeholder="https://..."
                className="bg-transparent border-white/20 text-white rounded-none"
                data-testid="collection-image-input"
              />
            </div>
          </div>
          <div className="mb-4">
            <label className="text-xs uppercase tracking-widest text-white/50 mb-1 block">Descripción</label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Breve descripción de la colección..."
              className="bg-transparent border-white/20 text-white rounded-none min-h-[80px]"
              data-testid="collection-desc-input"
            />
          </div>
          <div className="flex items-center gap-4 mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm(f => ({ ...f, active: e.target.checked }))} className="w-4 h-4 accent-[var(--primary)]" />
              <span className="text-white/70 text-sm">Activa</span>
            </label>
            <div className="flex items-center gap-2">
              <label className="text-white/50 text-sm">Orden:</label>
              <Input type="number" value={form.order} onChange={(e) => setForm(f => ({ ...f, order: parseInt(e.target.value) || 0 }))}
                className="bg-transparent border-white/20 text-white rounded-none w-20" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSave} className="btn-primary" data-testid="save-collection-btn">
              <Save size={14} className="mr-1" /> Guardar
            </Button>
            <Button variant="ghost" onClick={() => setEditing(null)} className="text-white/50">
              <X size={14} className="mr-1" /> Cancelar
            </Button>
          </div>
        </div>
      )}

      {/* Collections List */}
      <div className="space-y-2">
        {collections.length === 0 && !editing ? (
          <div className="text-center py-12 bg-[var(--surface)] border border-white/5">
            <Layers size={48} className="text-white/10 mx-auto mb-4" />
            <p className="text-white/40">No hay colecciones creadas</p>
          </div>
        ) : (
          collections.map(c => (
            <div key={c.id} className="flex items-center gap-4 p-4 bg-[var(--surface)] border border-white/5 hover:border-white/10 transition-colors" data-testid={`collection-item-${c.id}`}>
              {c.image_url && (
                <img src={c.image_url} alt="" className="w-16 h-10 object-cover flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-white font-medium truncate">{c.name}</span>
                  {!c.active && <span className="text-xs text-red-400 border border-red-400/30 px-1.5 py-0.5">Inactiva</span>}
                </div>
                <div className="flex items-center gap-3 text-xs text-white/40 mt-1">
                  <span className="flex items-center gap-1"><LinkIcon size={10} />/coleccion/{c.slug}</span>
                  <span className="font-mono bg-[var(--primary)]/10 text-[var(--primary)] px-1.5 py-0.5">etiqueta: {c.tag || c.slug}</span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <a href={`/coleccion/${c.slug}`} target="_blank" rel="noreferrer" className="p-2 text-white/30 hover:text-[var(--primary)] transition-colors" title="Ver página">
                  <Eye size={15} />
                </a>
                <button onClick={() => openEdit(c)} className="p-2 text-white/30 hover:text-white transition-colors" title="Editar">
                  <Edit2 size={15} />
                </button>
                <button onClick={() => handleDelete(c.id)} className="p-2 text-white/30 hover:text-red-500 transition-colors" title="Eliminar">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
